import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { MdReplay10, MdForward10, MdAspectRatio, MdAudiotrack, MdSubtitles, MdPictureInPictureAlt } from 'react-icons/md';
import { FiPlay, FiPause } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { isVideoInPictureInPicture, supportsDesktopPictureInPicture, togglePictureInPicture } from '../../utils/pictureInPicture';
import { getMediaKeyAction, MediaKeyAction } from '../../utils/tizenKeys';
import type { ExternalPlayback } from '../../utils/samsungAvPlayVod';
import { useModalKeyTrap } from '../../hooks/useModalKeyTrap';
import './VideoControlsOverlay.css';

/** Chrome 47 compat: play() returns undefined instead of a Promise */
function safePlay(video: HTMLVideoElement): void {
  const p = video.play();
  if (p !== undefined) p.catch(() => {});
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AudioTrackInfo  { id: number; name: string; lang?: string; }
export interface SubtitleTrackInfo { id: number; name: string; lang?: string; forced?: boolean; }

type BtnKey = 'aspect' | 'audio' | 'subtitle' | 'pip' | 'replay' | 'playpause' | 'forward';

interface VideoControlsOverlayProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  title: string;
  subtitle?: string;
  /** When true, keyboard input is not consumed — allows parent overlay to handle keys */
  blockInput?: boolean;
  /** Audio tracks available in the stream (show button only when length > 1) */
  audioTracks?: AudioTrackInfo[];
  activeAudioId?: number;
  onSelectAudio?: (trackId: number) => void | Promise<void>;
  /** Subtitle tracks available in the stream (show button only when length > 0) */
  subtitleTracks?: SubtitleTrackInfo[];
  activeSubtitleId?: number;
  onSelectSubtitle?: (trackId: number) => void | Promise<void>;
  externalPlayback?: ExternalPlayback;
}

type AspectMode = 'normal' | 'fill' | 'stretch' | '4:3' | '21:9';
type NormalizedKey = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight' | 'Enter' | 'Back' | MediaKeyAction | string;

const ASPECT_MODES: { key: AspectMode; labelKey: string; fallback: string }[] = [
  { key: 'normal',  labelKey: 'playback.aspectOriginal', fallback: 'Original' },
  { key: 'fill',    labelKey: 'playback.aspectFill', fallback: 'Fill' },
  { key: 'stretch', labelKey: 'playback.aspectStretch', fallback: 'Stretch' },
  { key: '4:3',     labelKey: 'playback.aspectFourThree', fallback: '4:3' },
  { key: '21:9',    labelKey: 'playback.aspectTwentyOneNine', fallback: '21:9' },
];

function getVideoBox(video: HTMLVideoElement) {
  const parent = video.parentElement;
  // Keep measurements in CSS pixels. getBoundingClientRect includes the
  // app-shell transform and would apply the TV scale twice.
  const width = Math.max(1, parent?.clientWidth || window.innerWidth || 1280);
  const height = Math.max(1, parent?.clientHeight || window.innerHeight || 720);
  return { width, height };
}

function fitBoxToRatio(containerWidth: number, containerHeight: number, ratio: number) {
  let width = containerHeight * ratio;
  let height = containerHeight;
  if (width > containerWidth) {
    width = containerWidth;
    height = containerWidth / ratio;
  }
  return { width: Math.round(width), height: Math.round(height) };
}

function applyAspectMode(video: HTMLVideoElement, mode: AspectMode) {
  const applyFixedRatio = (ratio: number) => {
    const box = getVideoBox(video);
    const fitted = fitBoxToRatio(box.width, box.height, ratio);
    video.style.objectFit = 'fill';
    video.style.width = `${fitted.width}px`;
    video.style.height = `${fitted.height}px`;
    video.style.margin = 'auto';
  };

  video.style.width = '100%';
  video.style.height = '100%';
  video.style.aspectRatio = '';
  video.style.margin = '0';
  switch (mode) {
    case 'normal':  video.style.objectFit = 'contain'; break;
    case 'fill':    video.style.objectFit = 'cover';   break;
    case 'stretch': video.style.objectFit = 'fill';    break;
    case '4:3':     applyFixedRatio(4 / 3); break;
    case '21:9':    applyFixedRatio(21 / 9); break;
  }
}

function resetVideoStyles(video: HTMLVideoElement) {
  video.style.objectFit  = '';
  video.style.width      = '';
  video.style.height     = '';
  video.style.aspectRatio = '';
  video.style.margin     = '';
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(secs: number): string {
  if (!Number.isFinite(secs) || secs < 0) return '0:00';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const HIDE_DELAY_MS = 5000;
const SEEK_COMMIT_DELAY_MS = 700;
const SEEK_HOLD_RESET_MS = 520;

function normalizeKey(e: KeyboardEvent): NormalizedKey {
  const key = e.key;
  const code = (e as any).keyCode || (e as any).which || 0;
  if (key === 'ArrowUp' || key === 'Up' || code === 38) return 'ArrowUp';
  if (key === 'ArrowDown' || key === 'Down' || code === 40) return 'ArrowDown';
  if (key === 'ArrowLeft' || key === 'Left' || code === 37) return 'ArrowLeft';
  if (key === 'ArrowRight' || key === 'Right' || code === 39) return 'ArrowRight';
  if (key === 'Enter' || key === 'OK' || key === ' ' || code === 13 || code === 32) return 'Enter';
  if (key === 'Escape' || key === 'BrowserBack' || key === 'GoBack' || code === 27 || code === 10009 || code === 461) return 'Back';
  const mediaAction = getMediaKeyAction(e);
  if (mediaAction) return mediaAction;
  return key;
}

function getAcceleratedSeekStep(elapsedMs: number): number {
  if (elapsedMs >= 10000) return 120;
  if (elapsedMs >= 5000) return 60;
  if (elapsedMs >= 3000) return 30;
  return 10;
}

function formatSeekStep(direction: -1 | 1, seconds: number): string {
  const sign = direction > 0 ? '+' : '-';
  if (seconds >= 60) {
    const minutes = seconds / 60;
    return `${sign}${Number.isInteger(minutes) ? minutes : minutes.toFixed(1)}m`;
  }
  return `${sign}${seconds}s`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function VideoControlsOverlay({
  videoRef, title, subtitle, blockInput,
  audioTracks = [], activeAudioId = 0,
  subtitleTracks = [], activeSubtitleId = -1,
  onSelectAudio, onSelectSubtitle,
  externalPlayback,
}: VideoControlsOverlayProps) {
  const { t } = useTranslation();
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(false);
  const visibleRef = useRef(false);
  visibleRef.current = visible;
  const [inFullscreen, setInFullscreen] = useState(false);
  const [pipActive, setPipActive] = useState(false);
  const inFullscreenRef = useRef(false);
  const [aspectModeIdx, setAspectModeIdx] = useState(0);
  const [trackSelector, setTrackSelector] = useState<'audio' | 'subtitle' | null>(null);
  const [trackOptionIdx, setTrackOptionIdx] = useState(0);
  const trackOptionIdxRef = useRef(0);
  const resumeAfterSelectorRef = useRef(false);
  const [trackStatusMessage, setTrackStatusMessage] = useState<string | null>(null);
  const trackStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pipAvailable = !externalPlayback && supportsDesktopPictureInPicture(videoRef.current);

  // Keep a ref so the fullscreenchange handler can read the latest mode
  const aspectModeIdxRef = useRef(0);

  // Dynamic button list based on available tracks
  const btnList = useMemo((): BtnKey[] => {
    const list: BtnKey[] = externalPlayback ? [] : ['aspect'];
    if (audioTracks.length > 1)    list.push('audio');
    if (subtitleTracks.length > 0) list.push('subtitle');
    if (pipAvailable) list.push('pip');
    list.push('replay', 'playpause', 'forward');
    return list;
  }, [audioTracks.length, externalPlayback, pipAvailable, subtitleTracks.length]);

  // Stable ref so keyboard handler doesn't go stale
  const btnListRef = useRef<BtnKey[]>(btnList);
  useEffect(() => { btnListRef.current = btnList; }, [btnList]);

  // Keyboard navigation: which button is focused (null = seek mode)
  // 0=aspect  1=replay10  2=playpause  3=forward10
  const [focusedBtnIdx, setFocusedBtnIdx] = useState<number | null>(null);
  const focusedBtnIdxRef = useRef<number | null>(null);

  // Scrub position: while the user holds arrow keys this diverges from currentTime.
  const [scrubPos, setScrubPos] = useState<number | null>(null);
  const [seekStepLabel, setSeekStepLabel] = useState<string | null>(null);

  const blockInputRef = useRef(false);
  useEffect(() => { blockInputRef.current = blockInput ?? false; }, [blockInput]);

  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seekCommitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seekHoldResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seekStepLabelTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seekHoldRef = useRef<{ direction: -1 | 1 | 0; startedAt: number; repeatCount: number }>({
    direction: 0,
    startedAt: 0,
    repeatCount: 0,
  });
  const progressTrackRef = useRef<HTMLDivElement>(null);

  const resetSeekHold = useCallback(() => {
    seekHoldRef.current = { direction: 0, startedAt: 0, repeatCount: 0 };
    if (seekHoldResetTimerRef.current) {
      clearTimeout(seekHoldResetTimerRef.current);
      seekHoldResetTimerRef.current = null;
    }
  }, []);

  // ── Auto-hide logic ─────────────────────────────────────────────────────────
  const showAndResetTimer = useCallback(() => {
    visibleRef.current = true;
    setVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      visibleRef.current = false;
      setVisible(false);
      setFocusedBtnIdx(null);
      focusedBtnIdxRef.current = null;
    }, HIDE_DELAY_MS);
  }, []);

  // ── Sync video events ───────────────────────────────────────────────────────
  useEffect(() => {
    if (externalPlayback) {
      const sync = () => {
        setCurrentTime(externalPlayback.currentTime());
        setDuration(externalPlayback.duration());
        setPaused(externalPlayback.paused());
      };
      sync();
      const timer = setInterval(sync, 250);
      return () => clearInterval(timer);
    }
    const video = videoRef.current;
    if (!video) return;

    const onTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      setDuration(video.duration || 0);
    };
    const onPlay = () => setPaused(false);
    const onPause = () => setPaused(true);
    const onDurationChange = () => setDuration(video.duration || 0);
    // When a programmatic seek completes, clear the scrub position
    const onSeeked = () => setScrubPos(null);

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('durationchange', onDurationChange);
    video.addEventListener('seeked', onSeeked);
    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('durationchange', onDurationChange);
      video.removeEventListener('seeked', onSeeked);
    };
  }, [externalPlayback, videoRef]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !pipAvailable) return;
    const update = () => setPipActive(isVideoInPictureInPicture(video));
    video.addEventListener('enterpictureinpicture', update);
    video.addEventListener('leavepictureinpicture', update);
    update();
    return () => {
      video.removeEventListener('enterpictureinpicture', update);
      video.removeEventListener('leavepictureinpicture', update);
    };
  }, [pipAvailable, videoRef]);

  // ── Fullscreen detection ────────────────────────────────────────────────────
  useEffect(() => {
    // Initialize from current state on mount — the component mounts AFTER
    // the parent sets isFullscreen=true, so the event has already fired.
    const checkFs = () => !!(
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement
    );

    if (checkFs()) {
      inFullscreenRef.current = true;
      setInFullscreen(true);
      if (videoRef.current) {
        applyAspectMode(videoRef.current, ASPECT_MODES[aspectModeIdxRef.current].key);
      }
      showAndResetTimer();
    }

    const onFsChange = () => {
      const isFs = checkFs();
      inFullscreenRef.current = isFs;
      setInFullscreen(isFs);
      if (isFs) {
        if (videoRef.current) {
          applyAspectMode(videoRef.current, ASPECT_MODES[aspectModeIdxRef.current].key);
        }
        showAndResetTimer();
      } else {
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        if (seekCommitTimerRef.current) clearTimeout(seekCommitTimerRef.current);
        if (videoRef.current) resetVideoStyles(videoRef.current);
        resetSeekHold();
        setVisible(false);
        setScrubPos(null);
        setSeekStepLabel(null);
        setFocusedBtnIdx(null);
        focusedBtnIdxRef.current = null;
      }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    document.addEventListener('mozfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
      document.removeEventListener('mozfullscreenchange', onFsChange);
    };
  }, [resetSeekHold, showAndResetTimer, videoRef]);

  // Re-apply fixed-ratio modes if the TV/browser resizes the fullscreen surface.
  useEffect(() => {
    const onResize = () => {
      if (!inFullscreenRef.current || !videoRef.current) return;
      applyAspectMode(videoRef.current, ASPECT_MODES[aspectModeIdxRef.current].key);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [videoRef]);

  // ── Scrub helper: move virtual position, schedule commit ───────────────────
  const scrubBy = useCallback((delta: number) => {
    const video = videoRef.current;
    const mediaDuration = externalPlayback?.duration() || video?.duration || 0;
    const mediaTime = externalPlayback?.currentTime() ?? video?.currentTime ?? 0;
    if (!mediaDuration) return;

    // Compute new scrub position from current scrub or real position
    setScrubPos(prev => {
      const base = prev !== null ? prev : mediaTime;
      return Math.max(0, Math.min(mediaDuration, base + delta));
    });

    // Reset commit timer
    if (seekCommitTimerRef.current) clearTimeout(seekCommitTimerRef.current);
    seekCommitTimerRef.current = setTimeout(() => {
      // Read the latest scrub position via a functional update
      setScrubPos(pos => {
        if (pos !== null) {
          if (externalPlayback) externalPlayback.seek(pos);
          else if (videoRef.current) videoRef.current.currentTime = pos;
        }
        return externalPlayback ? null : pos; // HTML video clears on 'seeked'
      });
    }, SEEK_COMMIT_DELAY_MS);
  }, [externalPlayback, videoRef]);

  const scrubWithAcceleration = useCallback((direction: -1 | 1) => {
    const now = Date.now();
    const hold = seekHoldRef.current;
    if (hold.direction !== direction || now - hold.startedAt > 12000) {
      seekHoldRef.current = { direction, startedAt: now, repeatCount: 1 };
    } else {
      hold.repeatCount += 1;
    }

    const activeHold = seekHoldRef.current;
    const elapsed = now - activeHold.startedAt;
    const step = getAcceleratedSeekStep(elapsed);
    setSeekStepLabel(formatSeekStep(direction, step));
    scrubBy(direction * step);

    if (seekHoldResetTimerRef.current) clearTimeout(seekHoldResetTimerRef.current);
    seekHoldResetTimerRef.current = setTimeout(resetSeekHold, SEEK_HOLD_RESET_MS);

    if (seekStepLabelTimerRef.current) clearTimeout(seekStepLabelTimerRef.current);
    seekStepLabelTimerRef.current = setTimeout(() => setSeekStepLabel(null), 900);
  }, [resetSeekHold, scrubBy]);

  // ── Aspect ratio cycle ──────────────────────────────────────────────────────
  const handleAspectCycle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setAspectModeIdx(prev => {
      const next = (prev + 1) % ASPECT_MODES.length;
      aspectModeIdxRef.current = next;
      if (videoRef.current) applyAspectMode(videoRef.current, ASPECT_MODES[next].key);
      return next;
    });
    showAndResetTimer();
  }, [videoRef, showAndResetTimer]);

  const closeTrackSelector = useCallback(() => {
    setTrackSelector(null);
    if (resumeAfterSelectorRef.current) {
      if (externalPlayback) externalPlayback.play();
      else if (videoRef.current) safePlay(videoRef.current);
    }
    resumeAfterSelectorRef.current = false;
    showAndResetTimer();
  }, [externalPlayback, showAndResetTimer, videoRef]);

  const openTrackSelector = useCallback((kind: 'audio' | 'subtitle') => {
    const video = videoRef.current;
    if (!video && !externalPlayback) return;
    const activeIndex = kind === 'audio'
      ? Math.max(0, audioTracks.findIndex(track => track.id === activeAudioId))
      : Math.max(0, subtitleTracks.findIndex(track => track.id === activeSubtitleId) + 1);
    resumeAfterSelectorRef.current = externalPlayback ? false : !video!.paused;
    if (!externalPlayback) video!.pause();
    trackOptionIdxRef.current = activeIndex;
    setTrackOptionIdx(activeIndex);
    setTrackSelector(kind);
    setVisible(true);
  }, [activeAudioId, activeSubtitleId, audioTracks, externalPlayback, subtitleTracks, videoRef]);

  const selectTrack = useCallback((kind: 'audio' | 'subtitle', trackId: number) => {
    const select = kind === 'audio' ? onSelectAudio : onSelectSubtitle;
    closeTrackSelector();
    if (!select) return;

    if (trackStatusTimerRef.current) clearTimeout(trackStatusTimerRef.current);
    trackStatusTimerRef.current = setTimeout(() => setTrackStatusMessage(t('playback.switchingTrack')), 500);
    Promise.resolve().then(() => select(trackId)).then(() => {
      if (trackStatusTimerRef.current) clearTimeout(trackStatusTimerRef.current);
      trackStatusTimerRef.current = null;
      setTrackStatusMessage(null);
    }).catch(() => {
      if (trackStatusTimerRef.current) clearTimeout(trackStatusTimerRef.current);
      setTrackStatusMessage(t('playback.trackError'));
      trackStatusTimerRef.current = setTimeout(() => setTrackStatusMessage(null), 3000);
    });
  }, [closeTrackSelector, onSelectAudio, onSelectSubtitle, t]);

  useModalKeyTrap(useCallback((key, e) => {
    if (!trackSelector) return;
    e.preventDefault();
    const optionCount = trackSelector === 'audio' ? audioTracks.length : subtitleTracks.length + 1;
    if (key === 'ArrowUp' || key === 'ArrowLeft' || key === 'ArrowDown' || key === 'ArrowRight') {
      const delta = key === 'ArrowUp' || key === 'ArrowLeft' ? -1 : 1;
      const next = Math.max(0, Math.min(optionCount - 1, trackOptionIdxRef.current + delta));
      trackOptionIdxRef.current = next;
      setTrackOptionIdx(next);
    } else if (key === 'Enter' || key === 'Space') {
      const index = trackOptionIdxRef.current;
      const trackId = trackSelector === 'audio'
        ? audioTracks[index]?.id
        : (index === 0 ? -1 : subtitleTracks[index - 1]?.id);
      if (typeof trackId === 'number') selectTrack(trackSelector, trackId);
    } else if (key === 'Back') {
      closeTrackSelector();
    }
  }, [audioTracks, closeTrackSelector, selectTrack, subtitleTracks, trackSelector]), Boolean(trackSelector));

  // ── Trigger a button by index (keyboard activation) ───────────────────────
  const triggerBtn = useCallback((idx: number) => {
    const video = videoRef.current;
    const key = btnListRef.current[idx];
    switch (key) {
      case 'aspect':
        setAspectModeIdx(prev => {
          const next = (prev + 1) % ASPECT_MODES.length;
          aspectModeIdxRef.current = next;
          if (video) applyAspectMode(video, ASPECT_MODES[next].key);
          return next;
        });
        break;
      case 'audio':
        if (audioTracks.length > 1) openTrackSelector('audio');
        break;
      case 'subtitle':
        if (subtitleTracks.length > 0) openTrackSelector('subtitle');
        break;
      case 'pip':
        void togglePictureInPicture(video).catch(() => {});
        break;
      case 'replay':
        scrubBy(-10);
        break;
      case 'playpause':
        if (externalPlayback) {
          if (externalPlayback.paused()) externalPlayback.play();
          else externalPlayback.pause();
        } else if (video) {
          if (video.paused) safePlay(video);
          else video.pause();
        }
        break;
      case 'forward':
        scrubBy(10);
        break;
    }
    showAndResetTimer();
  }, [audioTracks.length, externalPlayback, openTrackSelector, subtitleTracks.length, videoRef, scrubBy, showAndResetTimer]);

  // ── Keyboard handler ────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // If a parent overlay is handling input, don't consume the event
      if (blockInputRef.current) return;

      const video = videoRef.current;
      const key = normalizeKey(e);

      if (!inFullscreenRef.current) return;

      if (key === 'Back') {
        e.preventDefault(); e.stopImmediatePropagation();
        if (e.repeat) return;
        resetSeekHold();
        if (visibleRef.current) {
          if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
          visibleRef.current = false; setVisible(false);
          setFocusedBtnIdx(null); focusedBtnIdxRef.current = null;
        } else (document.exitFullscreen?.() ?? (document as any).webkitExitFullscreen?.());
        return;
      }

      showAndResetTimer();

      switch (key) {
        case 'ArrowUp':
        case 'ArrowDown': {
          e.preventDefault();
          e.stopImmediatePropagation();
          resetSeekHold();
          // Focus the play/pause button
          const ppIdx = btnListRef.current.indexOf('playpause');
          const target = ppIdx >= 0 ? ppIdx : 0;
          setFocusedBtnIdx(target);
          focusedBtnIdxRef.current = target;
          break;
        }
        case ' ':
        case 'Enter': {
          e.preventDefault();
          e.stopImmediatePropagation();
          resetSeekHold();
          const focused = focusedBtnIdxRef.current;
          if (focused !== null) {
            triggerBtn(focused);
          } else if (externalPlayback) {
            if (externalPlayback.paused()) externalPlayback.play();
            else externalPlayback.pause();
          } else if (video) {
            if (video.paused) safePlay(video);
            else video.pause();
          }
          break;
        }
        case 'ArrowLeft': {
          e.preventDefault();
          e.stopImmediatePropagation();
          const focused = focusedBtnIdxRef.current;
          if (focused !== null) {
            const next = Math.max(0, focused - 1);
            setFocusedBtnIdx(next);
            focusedBtnIdxRef.current = next;
            resetSeekHold();
          } else {
            scrubWithAcceleration(-1);
          }
          break;
        }
        case 'ArrowRight': {
          e.preventDefault();
          e.stopImmediatePropagation();
          const focused = focusedBtnIdxRef.current;
          if (focused !== null) {
            const next = Math.min(btnListRef.current.length - 1, focused + 1);
            setFocusedBtnIdx(next);
            focusedBtnIdxRef.current = next;
            resetSeekHold();
          } else {
            scrubWithAcceleration(1);
          }
          break;
        }
        case 'stop':
          e.preventDefault();
          e.stopImmediatePropagation();
          resetSeekHold();
          (document.exitFullscreen?.() ?? (document as any).webkitExitFullscreen?.());
          break;
        case 'play':
          e.preventDefault();
          e.stopImmediatePropagation();
          resetSeekHold();
          if (externalPlayback) externalPlayback.play();
          else if (video) safePlay(video);
          break;
        case 'pause':
          e.preventDefault();
          e.stopImmediatePropagation();
          resetSeekHold();
          if (externalPlayback) externalPlayback.pause();
          else video?.pause();
          break;
        case 'playPause': {
          e.preventDefault();
          e.stopImmediatePropagation();
          resetSeekHold();
          if (externalPlayback) {
            if (externalPlayback.paused()) externalPlayback.play();
            else externalPlayback.pause();
          } else if (video) {
            if (video.paused) safePlay(video);
            else video.pause();
          }
          showAndResetTimer();
          break;
        }
        case 'rewind':
        case 'fastForward':
          e.preventDefault();
          e.stopImmediatePropagation();
          scrubWithAcceleration(key === 'rewind' ? -1 : 1);
          break;
        default:
          break;
      }
    };

    const onKeyUp = () => resetSeekHold();

    window.addEventListener('keydown', handler, true);
    window.addEventListener('keyup', onKeyUp, true);
    return () => {
      window.removeEventListener('keydown', handler, true);
      window.removeEventListener('keyup', onKeyUp, true);
    };
  }, [audioTracks, closeTrackSelector, externalPlayback, resetSeekHold, scrubWithAcceleration, selectTrack, showAndResetTimer, subtitleTracks, triggerBtn, videoRef]);

  // ── Pointer move: show overlay ──────────────────────────────────────────────
  useEffect(() => {
    const onMove = () => {
      if (document.fullscreenElement) showAndResetTimer();
    };
    window.addEventListener('pointermove', onMove);
    return () => window.removeEventListener('pointermove', onMove);
  }, [showAndResetTimer]);

  // ── Cleanup timers on unmount ───────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (seekCommitTimerRef.current) clearTimeout(seekCommitTimerRef.current);
      if (seekHoldResetTimerRef.current) clearTimeout(seekHoldResetTimerRef.current);
      if (seekStepLabelTimerRef.current) clearTimeout(seekStepLabelTimerRef.current);
      if (trackStatusTimerRef.current) clearTimeout(trackStatusTimerRef.current);
    };
  }, []);

  // ── Button handlers ─────────────────────────────────────────────────────────
  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation();
    resetSeekHold();
    const video = videoRef.current;
    if (externalPlayback) {
      if (externalPlayback.paused()) externalPlayback.play();
      else externalPlayback.pause();
    } else if (video) {
      if (video.paused) safePlay(video);
      else video.pause();
    }
  };

  const handleReplay10 = (e: React.MouseEvent) => {
    e.stopPropagation();
    resetSeekHold();
    scrubBy(-10);
  };

  const handleForward10 = (e: React.MouseEvent) => {
    e.stopPropagation();
    resetSeekHold();
    scrubBy(10);
  };

  const handlePictureInPicture = (e: React.MouseEvent) => {
    e.stopPropagation();
    void togglePictureInPicture(videoRef.current).catch(() => {});
    showAndResetTimer();
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const video = videoRef.current;
    const track = progressTrackRef.current;
    if ((!video && !externalPlayback) || !track || !duration) return;
    const rect = track.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const pos = ratio * duration;
    resetSeekHold();
    setScrubPos(pos);
    if (externalPlayback) externalPlayback.seek(pos);
    else if (video) video.currentTime = pos;
    showAndResetTimer();
  };

  // Display: use scrubPos while scrubbing, otherwise real currentTime
  const displayTime = scrubPos !== null ? scrubPos : currentTime;
  const isScrubbing = scrubPos !== null;
  const progress = duration > 0 ? (displayTime / duration) * 100 : 0;

  // Always render (so effects/listeners are always active), but hide via CSS
  // when not in fullscreen or when auto-hidden
  const isVisible = inFullscreen && (visible || trackSelector !== null);

  return (
    <div
      className={`vco${isVisible ? '' : ' vco--hidden'}`}
      onClick={showAndResetTimer}
    >
      {/* Gradient scrim */}
      <div className="vco__gradient" />

      {seekStepLabel && (
        <div className="vco__seek-step" aria-hidden="true">
          {seekStepLabel}
        </div>
      )}

      {trackStatusMessage && (
        <div className="vco__track-status" aria-live="polite">{trackStatusMessage}</div>
      )}

      {trackSelector && (
        <div className="vco__track-modal" role="dialog" aria-modal="true" aria-label={t(trackSelector === 'audio' ? 'playback.selectAudio' : 'playback.selectSubtitle')}>
          <div className="vco__track-modal-title">{t(trackSelector === 'audio' ? 'liveTV.audio' : 'playback.subtitles')}</div>
          <div className="vco__track-options">
            {trackSelector === 'subtitle' && (
              <button
                type="button"
                className={`vco__track-option${trackOptionIdx === 0 ? ' vco__track-option--focused' : ''}${activeSubtitleId === -1 ? ' vco__track-option--active' : ''}`}
                onClick={(e) => { e.stopPropagation(); selectTrack('subtitle', -1); }}
              >
                <span>{t('liveTV.subtitleOff')}</span><span>{activeSubtitleId === -1 ? '✓' : ''}</span>
              </button>
            )}
            {(trackSelector === 'audio' ? audioTracks : subtitleTracks).map((track, index) => {
              const optionIndex = trackSelector === 'audio' ? index : index + 1;
              const active = trackSelector === 'audio' ? track.id === activeAudioId : track.id === activeSubtitleId;
              return (
                <button
                  key={`${trackSelector}-${track.id}`}
                  type="button"
                  className={`vco__track-option${trackOptionIdx === optionIndex ? ' vco__track-option--focused' : ''}${active ? ' vco__track-option--active' : ''}`}
                  onClick={(e) => { e.stopPropagation(); selectTrack(trackSelector, track.id); }}
                >
                  <span>
                    {track.name || t(trackSelector === 'audio' ? 'playback.audioTrack' : 'playback.subtitleTrack', { num: index + 1 })}
                    {'forced' in track && track.forced ? <span className="vco__track-forced">{t('playback.forcedSubtitle')}</span> : null}
                  </span><span>{active ? '✓' : ''}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* HUD content */}
      <div className="vco__hud">
        {/* Info row: title/subtitle on left, action buttons on right */}
        <div className="vco__info-row">
          <div className="vco__title-wrap">
            <div className="vco__title">{title}</div>
            {subtitle && <div className="vco__subtitle">{subtitle}</div>}
          </div>

          <div className="vco__actions">
            {/* Aspect ratio */}
            {!externalPlayback && <button
              className={`vco__btn vco__btn--aspect${btnList[focusedBtnIdx ?? -1] === 'aspect' ? ' vco__btn--focused' : ''}`}
              onClick={handleAspectCycle}
              aria-label={t('playback.aspectRatio', 'Aspect ratio')}
            >
              <MdAspectRatio className="vco__icon vco__icon--sm" />
              <span className="vco__btn-label">{t(ASPECT_MODES[aspectModeIdx].labelKey, ASPECT_MODES[aspectModeIdx].fallback)}</span>
            </button>}

            {/* Audio track */}
            {audioTracks.length > 1 && (() => {
              const idx = btnList.indexOf('audio');
              const activeLang = audioTracks.find(t => t.id === activeAudioId)?.lang?.slice(0, 3).toUpperCase() ?? '';
              return (
                <button
                  key="audio"
                  className={`vco__btn vco__btn--track${focusedBtnIdx === idx ? ' vco__btn--focused' : ''}`}
                  disabled={audioTracks.length <= 1}
                  onClick={(e) => { e.stopPropagation(); openTrackSelector('audio'); }}
                  aria-label={t('playback.selectAudio')}
                >
                  <span className="vco__track-icon-wrap">
                    <MdAudiotrack className="vco__icon vco__icon--sm" />
                    {activeLang && <span className="vco__track-badge">{activeLang}</span>}
                  </span>
                </button>
              );
            })()}

            {/* Subtitle track */}
            {subtitleTracks.length > 0 && (() => {
              const idx = btnList.indexOf('subtitle');
              const isOn = activeSubtitleId !== -1;
              const activeLang = isOn
                ? (subtitleTracks.find(t => t.id === activeSubtitleId)?.lang?.slice(0, 3).toUpperCase() ?? '✓')
                : '';
              return (
                <button
                  key="subtitle"
                  className={`vco__btn vco__btn--track${focusedBtnIdx === idx ? ' vco__btn--focused' : ''}${isOn ? ' vco__btn--track-active' : ''}`}
                  disabled={subtitleTracks.length === 0}
                  onClick={(e) => { e.stopPropagation(); openTrackSelector('subtitle'); }}
                  aria-label={t('playback.selectSubtitle')}
                >
                  <span className="vco__track-icon-wrap">
                    <MdSubtitles className="vco__icon vco__icon--sm" />
                    {activeLang && <span className="vco__track-badge">{activeLang}</span>}
                  </span>
                </button>
              );
            })()}

            {pipAvailable && (
              <button
                className={`vco__btn${pipActive ? ' vco__btn--track-active' : ''}${btnList[focusedBtnIdx ?? -1] === 'pip' ? ' vco__btn--focused' : ''}`}
                onClick={handlePictureInPicture}
                aria-label={t(pipActive ? 'playback.closePictureInPicture' : 'playback.openPictureInPicture')}
                title={t(pipActive ? 'playback.closePictureInPicture' : 'playback.pictureInPicture')}
              >
                <MdPictureInPictureAlt className="vco__icon vco__icon--sm" />
              </button>
            )}

            <button
              className={`vco__btn${btnList[focusedBtnIdx ?? -1] === 'replay' ? ' vco__btn--focused' : ''}`}
              onClick={handleReplay10}
              aria-label={t('playback.rewind10')}
            >
              <MdReplay10 className="vco__icon" />
            </button>

            <button
              className={`vco__btn vco__btn--primary${btnList[focusedBtnIdx ?? -1] === 'playpause' ? ' vco__btn--focused' : ''}`}
              onClick={handlePlayPause}
              aria-label={t(paused ? 'playback.play' : 'playback.pause')}
            >
              {paused ? <FiPlay className="vco__icon vco__icon--sm" /> : <FiPause className="vco__icon vco__icon--sm" />}
            </button>

            <button
              className={`vco__btn${btnList[focusedBtnIdx ?? -1] === 'forward' ? ' vco__btn--focused' : ''}`}
              onClick={handleForward10}
              aria-label={t('playback.forward10')}
            >
              <MdForward10 className="vco__icon" />
            </button>
          </div>
        </div>

        {/* Seeker row */}
        <div className="vco__seeker">
          <span className={`vco__time${isScrubbing ? ' vco__time--scrubbing' : ''}`}>
            {formatTime(displayTime)}
          </span>

          <div
            ref={progressTrackRef}
            className={`vco__progress-track${isScrubbing ? ' vco__progress-track--scrubbing' : ''}`}
            onClick={handleProgressClick}
          >
            <div
              className="vco__progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>

          <span className="vco__time">{formatTime(duration)}</span>
        </div>
      </div>
    </div>
  );
}
